
import { useMemo } from 'react';

interface User {
  id: string;
  username: string;
  city: string;
  state_code: string;
  main_photo_url?: string;
  is_online: boolean;
  onboarding_completed: boolean;
  hidden: boolean;
  created_at: string;
  verification_level: string;
  last_seen?: string;
  last_login?: string;
  user_role?: string;
  last_activity_at?: string;
  plan_type: string;
}

export type SortOption = 'created_at' | 'last_login' | 'username' | 'role' | 'online_status' | 'plan_type';

interface SortConfig {
  field: SortOption;
  direction: 'asc' | 'desc';
}

export function useUserSorting(users: User[], sortConfig: SortConfig) {
  return useMemo(() => {
    const sorted = [...users].sort((a, b) => {
      let compareResult = 0;

      switch (sortConfig.field) {
        case 'created_at':
          compareResult = new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
          break;
        case 'last_login':
          const aLogin = a.last_login ? new Date(a.last_login).getTime() : 0;
          const bLogin = b.last_login ? new Date(b.last_login).getTime() : 0;
          compareResult = bLogin - aLogin;
          break;
        case 'username':
          compareResult = (a.username || '').localeCompare(b.username || '');
          break;
        case 'role':
          const roleOrder = { admin: 3, moderator: 2, user: 1 };
          const aRoleValue = roleOrder[(a.user_role as keyof typeof roleOrder) || 'user'] || 1;
          const bRoleValue = roleOrder[(b.user_role as keyof typeof roleOrder) || 'user'] || 1;
          compareResult = bRoleValue - aRoleValue;
          break;
        case 'online_status':
          compareResult = (b.is_online ? 1 : 0) - (a.is_online ? 1 : 0);
          break;
        case 'plan_type':
          // Order: premium, standard, free
          const planOrder = { premium: 3, standard: 2, free: 1 };
          const aPlanValue = planOrder[a.plan_type as keyof typeof planOrder] || 0;
          const bPlanValue = planOrder[b.plan_type as keyof typeof planOrder] || 0;
          compareResult = bPlanValue - aPlanValue;
          break;
      }

      return sortConfig.direction === 'desc' ? compareResult : -compareResult;
    });

    return sorted;
  }, [users, sortConfig]);
}
